import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import { formatFileSize } from '@/helpers/file.helper.ts';
import type { MediaUploadPreflightResponse } from '@/types/dtos/media/media-upload.dto.ts';
import { shouldShowUploadQuotaPreflightLines } from '@/helpers/media-upload-preflight-display.ts';
import { resolveMediaUploadBlockedMessage } from '@/helpers/media-upload-preflight.helper.ts';

export default function MediaUploadPreflightSummary({
  fileName,
  fileSizeBytes,
  preflight,
  onDismiss,
}: {
  fileName: string;
  fileSizeBytes: number;
  preflight: MediaUploadPreflightResponse;
  onDismiss?: () => void;
}) {
  const t = useTranslations();

  const showQuota = shouldShowUploadQuotaPreflightLines(preflight);
  const sizeLimitMessage =
    preflight.maxFileSizeBytes != null &&
    (preflight.errorCode === 'FILE_TOO_LARGE' || fileSizeBytes > preflight.maxFileSizeBytes)
      ? resolveMediaUploadBlockedMessage(preflight, fileSizeBytes, fileName, t)
      : null;

  const sequenceCount = preflight.details?.imageSequenceTotal;
  const fileLabel =
    sequenceCount != null && sequenceCount > 1
      ? t('media.uploadPreflight.sequenceFileLabel', {
          count: sequenceCount,
          totalSize: formatFileSize(fileSizeBytes),
        })
      : t('media.uploadPreflight.fileLabel', {
          name: fileName,
          size: formatFileSize(fileSizeBytes),
        });

  return (
    <Box sx={{ width: '100%', mt: 1, mb: 0.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
        <Typography variant="body2" fontWeight={500} noWrap title={fileName} sx={{ flex: 1, minWidth: 0 }}>
          {t('media.uploadPreflight.title')}
        </Typography>
        {!preflight.allowed && onDismiss != null && (
          <Button size="small" color="inherit" onClick={onDismiss} sx={{ flexShrink: 0 }}>
            {t('media.uploadProgress.cancel')}
          </Button>
        )}
      </Stack>
      <Stack spacing={0.25} sx={{ mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {fileLabel}
        </Typography>
        {showQuota && (
          <>
            <Typography variant="caption" color="text.secondary">
              {t('media.uploadPreflight.quotaAvailable', {
                amount: formatFileSize(preflight.availableBytes),
              })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('media.uploadPreflight.quotaAfter', {
                amount: formatFileSize(preflight.remainingAfterBytes),
              })}
            </Typography>
            {(preflight.reservedBytesInFlight ?? 0) > 0 && (
              <Typography variant="caption" color="text.secondary">
                {t('media.uploadPreflight.quotaReservedInFlight', {
                  amount: formatFileSize(preflight.reservedBytesInFlight!),
                })}
              </Typography>
            )}
          </>
        )}
        {sizeLimitMessage != null && preflight.allowed && (
          <Alert severity="warning" sx={{ py: 0, mt: 0.5 }}>
            {sizeLimitMessage}
          </Alert>
        )}
        {!preflight.allowed && (
          <Alert severity="error" sx={{ py: 0, mt: 0.5 }}>
            {sizeLimitMessage ?? t('media.uploadPreflight.quotaInsufficient')}
          </Alert>
        )}
      </Stack>
    </Box>
  );
}
